import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchCharVariationSamples } from '../../../slices/userLitGroupAssetsSlice';
import {
  // Thunks
  fetchCharacters,
  fetchCharacter,
  createCharacter,
  updateCharacter
} from '../../slices/voiceActorDataSlice';
import { useReduxSelectorsZoneBased } from '../core/useReduxSelectorsZoneBased';

/**
 * 🚀 ZONE-BASED Character operations hook for VoiceActor - only subscribes to Character Zone data
 * This prevents unnecessary re-renders by avoiding Variation Zone data subscriptions
 * Mirrors BookWorksheet's useCharacterOperationsZoneBased with VoiceActor entity adaptations
 * @returns {Object} Object containing Character state and operations
 */
export const useCharacterOperationsZoneBased = () => {
  const dispatch = useDispatch();

  // 🚀 ZONE-BASED: Get relevant state from Zone-Based Redux selectors (Character Zone only)
  const {
    actorId,           // instead of litGroupId
    portfolioId,       // instead of bookId
    characterId,
    selectedCharacter,
    characters,
    charactersLoading,
    charactersError,
    charactersLastUpdated
  } = useReduxSelectorsZoneBased();

  // Refs to track the last entity IDs we fetched data for
  // This helps prevent infinite loops when fetching data
  const lastFetchedPortfolioId = React.useRef(null);

  // Fetch functions with cache invalidation
  const fetchCharactersWithCache = useCallback((portfolioId, force = false) => {
    if (!portfolioId) {
      console.warn('[VA_FETCH] Cannot fetch characters: No portfolio ID provided');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    // Simple cache check - if we just fetched for this Portfolio and we're not forcing, use cache
    if (lastFetchedPortfolioId.current === portfolioId && !force) {
      console.log(`[VA_FETCH] Already fetched characters for portfolio ${portfolioId} recently, using cache`);
      return Promise.resolve({ type: 'cache/hit' });
    }

    // Check if we need to fetch based on cache or portfolio change
    const now = Date.now();
    const cacheTime = 5 * 60 * 1000; // 5 minutes

    // Force a fetch if:
    // 1. force is true, or
    // 2. the cache is older than 5 minutes, or
    // 3. we haven't fetched for this portfolio yet
    if (force || !charactersLastUpdated || (now - charactersLastUpdated > cacheTime) || lastFetchedPortfolioId.current !== portfolioId) {
      console.log(`[VA_FETCH] Fetching characters for portfolio ${portfolioId} (cache invalid or forced)`);

      // Update the last fetched Portfolio ID
      lastFetchedPortfolioId.current = portfolioId;

      return dispatch(fetchCharacters(portfolioId));
    } else {
      console.log(`[VA_FETCH] Using cached characters for portfolio ${portfolioId}`);
      return Promise.resolve({ type: 'cache/hit' });
    }
  }, [dispatch, charactersLastUpdated]);

  const fetchCharacterWithCache = useCallback((characterId, portfolioId, force = true) => {
    if (!characterId) {
      console.warn('[VA_FETCH] Cannot fetch character: No character ID provided');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    // For characters, we always force a fresh fetch when explicitly requested
    // This is because character data changes frequently
    if (force) {
      console.log(`[VA_FETCH] Fetching character ${characterId} (forced)`);

      return dispatch(fetchCharacter({ characterId, portfolioId }));
    } else {
      // Check if the character exists in the store
      if (selectedCharacter && selectedCharacter.uuid === characterId) {
        console.log(`[VA_FETCH] Using cached character ${characterId}`);
        return Promise.resolve({ type: 'cache/hit' });
      } else {
        console.log(`[VA_FETCH] Fetching character ${characterId} (not in cache)`);

        return dispatch(fetchCharacter({ characterId, portfolioId }));
      }
    }
  }, [dispatch, selectedCharacter]);

  // Function to fetch character data with variations and speech samples
  const fetchCharacterWithSamples = useCallback(async (characterId, portfolioId) => {
    if (!characterId) {
      console.warn('[VA_FETCH] Cannot fetch character samples: No character ID provided');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    if (!actorId) {
      console.warn('[VA_FETCH] Cannot fetch character samples: No actor ID provided');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    try {
      console.log(`[VA_FETCH] Fetching character ${characterId} with samples`);

      // Use the fetchCharVariationSamples thunk adapted for VoiceActor entities
      const result = await dispatch(fetchCharVariationSamples({
        actorUuid: actorId,        // instead of litGroupUuid
        portfolioUuid: portfolioId, // instead of bookUuid
        charUuid: characterId
      })).unwrap();

      return result;
    } catch (error) {
      console.error(`[VA_FETCH] Error fetching character with samples:`, error);
      throw error;
    }
  }, [dispatch, actorId]);

  // Character CRUD operations
  const createNewCharacter = useCallback((data) => {
    if (!portfolioId) {
      console.warn('[VA_CREATE] Cannot create character: No portfolio selected');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    return dispatch(createCharacter({ portfolioId, data }));
  }, [dispatch, portfolioId]);

  const updateExistingCharacter = useCallback((data) => {
    if (!data.characterId) {
      console.warn('[VA_UPDATE] Cannot update character: No character selected');
      return Promise.resolve({ type: 'error/missing-id' });
    }

    return dispatch(updateCharacter({ characterId: data.characterId, portfolioId, data }));
  }, [dispatch, characterId, portfolioId]);

  return {
    // State - Character Zone only
    selectedCharacter,
    characters,
    charactersLoading,
    charactersError,

    // Fetch operations - Character Zone only
    fetchCharacters: fetchCharactersWithCache,
    fetchCharacter: fetchCharacterWithCache,
    fetchCharacterWithSamples,

    // CRUD operations - Character Zone only
    createCharacter: createNewCharacter,
    updateCharacter: updateExistingCharacter
  };
};

export default useCharacterOperationsZoneBased;
